/**
 * Test harness page for isolated Lexical baseline editor testing
 * Only rendered when __LEXICAL_TEST_MODE__=true for E2E test coverage
 *
 * Epic 06 Scope 02 Enhancement:
 * Now mounts real CleanSongEditor with working save handlers for E2E validation
 * of the complete save pipeline (AppHeader → Save Button → Serializer → API)
 */

import RichTextLyricsEditor from '../../components/RichTextLyricsEditor'
import CleanSongEditor from '../../components/lexical/CleanSongEditor'
import { useCanonicalLyrics } from '../../components/lexical/hooks/useCanonicalLyrics'
import type { LexicalLyricsEditorRef } from '../../components/LexicalLyricsEditor'
import { useRef, useState, useEffect, useCallback } from 'react'
import { AppHeader } from '../../components/layout/AppHeader'
import { SectionToolbar } from '../../components/SectionToolbar'
import { usePanelState } from '../../hooks/usePanelState'
import type { SectionType } from '../../utils/sectionUtils'
import type { AutoSaveStatus } from '../../components/editor/AutoSaveIndicator'
import type { Song } from '../../lib/api'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    __lexicalEditor?: any // Raw LexicalEditor instance for E2E programmatic access
    __lexicalUtils?: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      $getRoot: () => any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      $getSelection: () => any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      $isRangeSelection: (selection: any) => boolean
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      $createParagraphNode: () => any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      $createTextNode: (text: string) => any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      $selectAll: () => any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      $createRangeSelection: () => any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      $setSelection: (selection: any) => void
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      SELECT_ALL_COMMAND: any
    }
    __lexicalNodes?: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      LyricsTextNode: any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      $createLyricsTextNode: (text: string) => any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      $setStressPattern: (node: any, word: string, pattern: any) => void
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      $isLyricsTextNode: (node: any) => boolean
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      $getAllStressPatterns: (node: any) => any
    }
  }
}

export function TestEditorPage() {
  const editorRef = useRef<LexicalLyricsEditorRef | null>(null)
  const songEditorRef = useRef<{ triggerSave: () => Promise<void> } | null>(null)
  const isTestEnv = import.meta.env.MODE === 'test' || import.meta.env.DEV
  const panelState = usePanelState()

  // Check for test_mode query parameter (baseline, stress, stress_health, or full)
  const searchParams = new URLSearchParams(window.location.search)
  const testMode = searchParams.get('test_mode')
  const isBaselineMode = testMode === 'baseline'
  const isStressMode = testMode === 'stress'
  const isStressHealthMode = testMode === 'stress_health' // Stress features WITH health gating (no bypass)
  const isFullMode = testMode === 'full' // Full CleanSongEditor with save pipeline

  // Stability level for rhyme scheme testing (1-10, where 1=varied/loose, 10=rigid/stable)
  const stabilityLevelParam = searchParams.get('stability_level')
  const stabilityLevel = stabilityLevelParam ? parseInt(stabilityLevelParam, 10) : undefined
  // Convert stability level (1-10) to rhythmicStability (0-1)
  // Level 1 (varied) → rhythmicStability 0.0 (loose)
  // Level 10 (rigid) → rhythmicStability 1.0 (stable)
  const rhythmicStability = stabilityLevel !== undefined
    ? (stabilityLevel - 1) / 9
    : undefined

  // Opening move: mock song state for AppHeader
  const [testSong, setTestSong] = useState<Song>({
    id: 'test-song-id',
    user_id: 'test-user-id',
    title: 'Test Song',
    artist: 'Test Artist',
    status: 'draft' as const,
    lyrics: '',
    tags: [],
    settings: {
      narrative_pov: 'first_person',
      six_best_friends: {},
      structural_boxes: [],
      section_structure: [],
      rhyme_preferences: {
        primary_types: [],
        allow_slant_rhymes: true,
        emphasis_on_perfect: false,
      },
      prosody_settings: {
        rhythmic_stability: 5,
        phrasing_style: 'balanced',
        syllable_emphasis: true,
        meter_consistency: 5,
      },
      keyword_settings: {
        primary_keywords: [],
        metaphor_themes: [],
        avoid_words: [],
        synonym_groups: {},
      },
      style_guide: {
        sub_genres: [],
        artist_references: [],
        avoid_cliches: true,
        innovation_level: 5,
      },
      energy_level: 5,
      ai: {
        creativity_level: 5,
        preserve_user_phrases: true,
        auto_suggestions: true,
        suggestion_frequency: 3,
        writing_style_adaptation: true,
        ai_feedback_preferences: {
          stability_preference: 5,
          genre_context: null,
          custom_genre: null,
          feedback_verbosity: 'medium',
          enabled_features: {
            rhyme: true,
            prosody: true,
            structure: true,
          },
        },
      },
    },
    metadata: {},
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  })

  // Big play: state for save flow tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('saved')
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error' | 'offline' | undefined>(undefined)

  // Main play: mock handlers for SectionToolbar (baseline mode only)
  const handleInsertSection = useCallback((sectionType: SectionType) => {
    console.log('🎯 TEST-PAGE: Insert section requested:', sectionType)
    // E2E tests can override this behavior if needed
  }, [])

  const handleShowSectionNav = useCallback(() => {
    console.log('🎯 TEST-PAGE: Section nav requested')
  }, [])

  // Victory lap: Real save handler for full mode
  const handleManualSave = useCallback(async () => {
    console.log('🎯 TEST-PAGE: Manual save triggered')
    if (songEditorRef.current && isFullMode) {
      setIsSaving(true)
      try {
        await songEditorRef.current.triggerSave()
        console.log('🎯 TEST-PAGE: Save completed successfully')
      } catch (error) {
        console.error('🎯 TEST-PAGE: Save failed:', error)
      } finally {
        setIsSaving(false)
      }
    } else {
      console.log('🎯 TEST-PAGE: Save requested (no-op in baseline mode)')
    }
  }, [isFullMode])

  // Handler for song changes from CleanSongEditor
  const handleSongChange = useCallback((song: Song) => {
    console.log('🎯 TEST-PAGE: Song changed:', song.id)
    setTestSong(song)
  }, [])

  const handleSongLoaded = useCallback((song: Song) => {
    console.log('🎯 TEST-PAGE: Song loaded:', song.id)
    setTestSong(song)
  }, [])

  const handleSongUpdate = useCallback((updates: Partial<Song>) => {
    console.log('🎯 TEST-PAGE: Song metadata updated:', updates)
    setTestSong(prev => ({ ...prev, ...updates }))
  }, [])

  const handleSaveStatusChange = useCallback((status: 'saved' | 'saving' | 'error') => {
    console.log('🎯 TEST-PAGE: Save status changed:', status)
    setSaveStatus(status)
  }, [])

  const handleAutoSaveStatusChange = useCallback((status: AutoSaveStatus) => {
    console.log('🎯 TEST-PAGE: Auto-save status changed:', status)
    setAutoSaveStatus(status)
  }, [])

  const handleHasUnsavedChangesChange = useCallback((hasChanges: boolean) => {
    console.log('🎯 TEST-PAGE: Unsaved changes:', hasChanges)
    setHasUnsavedChanges(hasChanges)
  }, [])

  const handleBack = useCallback(() => {
    console.log('🎯 TEST-PAGE: Back button clicked')
  }, [])

  const handleEditorRef = useCallback((instance: LexicalLyricsEditorRef | null) => {
    editorRef.current = instance
    if (isTestEnv && instance) {
      // Big play: Use onEditorReady to get access to raw editor instance
      // Tests expect window.__lexicalEditor to have update() method
      instance.onEditorReady(() => {
        const wysiwygEl = instance.getWysiwygElement()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (wysiwygEl && (wysiwygEl as any).__lexicalEditor) {
          // Victory lap: Expose raw Lexical editor instance for E2E programmatic access
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          window.__lexicalEditor = (wysiwygEl as any).__lexicalEditor
        }
      })
    } else if (isTestEnv) {
      window.__lexicalEditor = null
    }
  }, [isTestEnv])

  // Load canonical lyrics (but won't use them in baseline mode)
  const { lyrics, isLoading, error } = useCanonicalLyrics()
  const [_content, setContent] = useState('')

  // Big play: expose editor ref and Lexical helpers globally for Playwright helpers
  useEffect(() => {
    if (isTestEnv) {
      import('lexical').then((lexical) => {
        window.__lexicalUtils = {
          $getRoot: lexical.$getRoot,
          $getSelection: lexical.$getSelection,
          $isRangeSelection: lexical.$isRangeSelection,
          $createParagraphNode: lexical.$createParagraphNode,
          $createTextNode: lexical.$createTextNode,
          $selectAll: lexical.$selectAll,
          $createRangeSelection: lexical.$createRangeSelection,
          $setSelection: lexical.$setSelection,
          SELECT_ALL_COMMAND: lexical.SELECT_ALL_COMMAND,
        }
      })
    }

    return () => {
      if (isTestEnv) {
        window.__lexicalEditor = null
        window.__lexicalUtils = undefined
      }
    }
  }, [isTestEnv])

  // Opening move: expose custom node helpers for E2E tests that inject test data
  useEffect(() => {
    if (isTestEnv) {
      import('../../components/lexical/nodes/LyricsTextNode').then((module) => {
        window.__lexicalNodes = {
          LyricsTextNode: module.LyricsTextNode,
          $createLyricsTextNode: module.$createLyricsTextNode,
          $setStressPattern: module.$setStressPattern,
          $isLyricsTextNode: module.$isLyricsTextNode,
          $getAllStressPatterns: module.$getAllStressPatterns,
        }
      })
    }

    return () => {
      if (isTestEnv) {
        window.__lexicalNodes = undefined
      }
    }
  }, [isTestEnv])

  // Big play: In baseline, stress, or stress_health mode, skip the loading state
  // All test modes start with empty editor for test control
  if (!isBaselineMode && !isStressMode && !isStressHealthMode && isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 p-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl text-white mb-4">Lexical Baseline Editor Test Page</h1>
          <p className="text-gray-400 mb-4">Loading canonical lyrics...</p>
        </div>
      </div>
    )
  }

  // Main play: Determine initial content based on test mode
  // baseline/stress/stress_health mode → empty editor (tests control data)
  // normal mode → canonical lyrics for manual testing
  const initialContent = (isBaselineMode || isStressMode || isStressHealthMode) ? '' : lyrics

  // Opening move: if full mode, render CleanSongEditor with AppHeader
  if (isFullMode) {
    return (
      <div className="h-screen flex flex-col" style={{ background: 'var(--editor-bg-primary)' }}>
        <AppHeader
          panelState={panelState}
          currentSong={testSong}
          saveStatus={saveStatus}
          isEditorMode={true}
          onBack={handleBack}
          onSongUpdate={handleSongUpdate}
          hasUnsavedChanges={hasUnsavedChanges}
          onSave={handleManualSave}
          isSaving={isSaving}
          autoSaveStatus={autoSaveStatus}
        />
        <div className="flex-1 overflow-hidden">
          <CleanSongEditor
            songId="test-song-id"
            song={testSong}
            onSongChange={handleSongChange}
            onSongLoaded={handleSongLoaded}
            onSongUpdate={handleSongUpdate}
            onClose={handleBack}
            onSaveStatusChange={handleSaveStatusChange}
            onHasUnsavedChangesChange={handleHasUnsavedChangesChange}
            onAutoSaveStatusChange={handleAutoSaveStatusChange}
            ref={songEditorRef}
          />
        </div>
      </div>
    )
  }

  // Main play: baseline/stress mode renders simple editor with mock toolbar
  return (
    <div className="min-h-screen bg-white">
      {/* Victory lap: render AppHeader and SectionToolbar for E2E test compatibility */}
      {isBaselineMode && (
        <>
          <AppHeader
            panelState={panelState}
            currentSong={testSong}
            isEditorMode={true}
            onBack={handleBack}
            onSave={handleManualSave}
            hasUnsavedChanges={hasUnsavedChanges}
            isSaving={isSaving}
            autoSaveStatus={autoSaveStatus}
          />
          <div data-testid="section-toolbar">
            <SectionToolbar
              onInsertSection={handleInsertSection}
              onShowSectionNav={handleShowSectionNav}
              hasExistingSections={false}
              sections={[]}
            />
          </div>
        </>
      )}

      <div className="max-w-4xl mx-auto p-8">
        <h1 className="text-2xl text-gray-900 mb-4">
          Lexical Baseline Editor Test Page
          {isBaselineMode && <span className="text-sm text-gray-500 ml-2">(baseline mode)</span>}
          {isStressMode && <span className="text-sm text-gray-500 ml-2">(stress mode)</span>}
        </h1>
        {error && <p className="text-red-600 mb-4">Error loading lyrics: {error.message}</p>}
        <div className="bg-white border border-gray-300 rounded-lg p-4" data-testid="lyrics-editor">
          <RichTextLyricsEditor
            ref={handleEditorRef}
            value={initialContent}
            onChange={setContent}
            placeholder="Type your lyrics here..."
            enableStressMarking={isStressMode || isStressHealthMode}
            bypassAnalyzerHealthCheck={isStressMode}  // Only bypass in stress mode, NOT stress_health
            rhythmicStability={rhythmicStability}
          />
        </div>
      </div>
    </div>
  )
}
